import type { MfgMaterial } from "@/lib/manufacturingClient";
import { sumLineAmounts } from "@/lib/manufacturing/purchaseOrderTypes";
import {
  draftLineAmount,
  newDraftLine,
  type PoDraftLine,
} from "@/components/manufacturing/purchaseOrderDraftState";
import {
  ghostBtnClass,
  inputClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";

export function GeneratePurchaseOrderLinesEditor(props: {
  lines: PoDraftLine[];
  onChange: (lines: PoDraftLine[]) => void;
  materials: MfgMaterial[];
  enabled: boolean;
  busy: boolean;
  readOnlyStructure: boolean;
}) {
  const { lines, onChange } = props;
  const previewTotal = sumLineAmounts(lines.map((l) => ({ amount: draftLineAmount(l) })));

  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold">Lines</div>
      {lines.map((line, idx) => (
        <div
          key={line.key}
          className="grid gap-1 rounded-lg border border-gray-100 p-2 dark:border-zinc-800 sm:grid-cols-6"
        >
          <label className="text-[11px] sm:col-span-2">
            Material
            <input
              list="po-material-options"
              className={`${inputClass} mt-0.5 w-full`}
              value={line.material_name}
              disabled={!props.enabled || props.busy || props.readOnlyStructure}
              onChange={(e) => {
                const name = e.target.value;
                const match = props.materials.find(
                  (m) => m.name === name || m.code === name
                );
                onChange(
                  lines.map((l, i) =>
                    i === idx
                      ? { ...l, material_name: name, material_id: match?.id ?? null }
                      : l
                  )
                );
              }}
            />
          </label>
          <label className="text-[11px] sm:col-span-2">
            Description
            <input
              className={`${inputClass} mt-0.5 w-full`}
              value={line.description}
              disabled={!props.enabled || props.busy || props.readOnlyStructure}
              onChange={(e) =>
                onChange(
                  lines.map((l, i) => (i === idx ? { ...l, description: e.target.value } : l))
                )
              }
            />
          </label>
          <label className="text-[11px]">
            Qty *
            <input
              className={`${inputClass} mt-0.5 w-full`}
              value={line.quantity}
              disabled={!props.enabled || props.busy || props.readOnlyStructure}
              onChange={(e) =>
                onChange(
                  lines.map((l, i) => (i === idx ? { ...l, quantity: e.target.value } : l))
                )
              }
            />
          </label>
          <label className="text-[11px]">
            Rate
            <input
              className={`${inputClass} mt-0.5 w-full`}
              value={line.rate}
              disabled={!props.enabled || props.busy || props.readOnlyStructure}
              onChange={(e) =>
                onChange(lines.map((l, i) => (i === idx ? { ...l, rate: e.target.value } : l)))
              }
            />
          </label>
          <label className="text-[11px] sm:col-span-2">
            Expected pieces (internal)
            <input
              className={`${inputClass} mt-0.5 w-full`}
              value={line.expected_pieces}
              disabled={!props.enabled || props.busy || props.readOnlyStructure}
              onChange={(e) =>
                onChange(
                  lines.map((l, i) =>
                    i === idx ? { ...l, expected_pieces: e.target.value } : l
                  )
                )
              }
            />
          </label>
          <div className="self-end pb-1 text-[11px] sm:col-span-2">
            Amount:{" "}
            <span className="font-semibold">
              {draftLineAmount(line) == null ? "—" : `$${draftLineAmount(line)!.toFixed(2)}`}
            </span>
          </div>
          <div className="flex items-end justify-end sm:col-span-2">
            <button
              type="button"
              className={ghostBtnClass}
              disabled={props.busy || lines.length <= 1 || props.readOnlyStructure}
              onClick={() => onChange(lines.filter((_, i) => i !== idx))}
            >
              Remove
            </button>
          </div>
        </div>
      ))}
      <datalist id="po-material-options">
        {props.materials.map((m) => (
          <option key={m.id} value={m.name} />
        ))}
      </datalist>
      {!props.readOnlyStructure ? (
        <button
          type="button"
          className={ghostBtnClass}
          disabled={props.busy}
          onClick={() => onChange([...lines, newDraftLine()])}
        >
          + Add line
        </button>
      ) : null}
      <div className="text-right text-sm font-semibold">
        Total preview: {previewTotal == null ? "—" : `$${previewTotal.toFixed(2)}`}
      </div>
    </div>
  );
}
